import { z } from 'zod';

/**
 * Wire format the hera agent pushes from each runner machine (macOS and Windows
 * send the same shape). All sizes are bytes, all rates bytes/second — the agent
 * computes rates from its own counter deltas so the server never has to.
 */
export const RunnerMachineOsSchema = z.enum(['macos', 'windows']);
export type RunnerMachineOs = z.infer<typeof RunnerMachineOsSchema>;

const bytes = z.number().int().nonnegative();
const UsageSchema = z.object({ usedBytes: bytes, totalBytes: bytes });

/** `owner/repo` as GitHub spells it. */
export const RepoFullNameSchema = z
  .string()
  .regex(/^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/, 'Expected owner/repo');

export const AgentInfoSchema = z.object({
  hostname: z.string().min(1).max(255),
  os: RunnerMachineOsSchema,
  arch: z.string().min(1).max(32),
  agentVersion: z.string().min(1).max(32),
});
export type AgentInfo = z.infer<typeof AgentInfoSchema>;

/** A runner installed on the machine (from its `.runner` file). */
export const RunnerProcSchema = z.object({
  name: z.string().min(1).max(255),
  repo: RepoFullNameSchema,
  /** Process-tree usage of the runner (listener + worker + job steps); null when not running. */
  cpuPct: z.number().min(0).nullable(),
  rssBytes: bytes.nullable(),
});
export type RunnerProc = z.infer<typeof RunnerProcSchema>;

export const MachineSampleSchema = z.object({
  ts: z.string().datetime(),
  cpuPct: z.number().min(0).max(100),
  mem: UsageSchema,
  swap: UsageSchema,
  disks: z.array(UsageSchema.extend({ mount: z.string().min(1).max(255) })).max(32),
  diskIo: z.object({ readBps: bytes, writeBps: bytes }),
  net: z.object({ rxBps: bytes, txBps: bytes }),
  runners: z.array(RunnerProcSchema).max(64),
});
export type MachineSample = z.infer<typeof MachineSampleSchema>;

/** One hour of 15s samples — the agent's offline buffer size. */
export const MAX_SAMPLES_PER_BATCH = 240;
